<template>
  <div ref="searchResultsWrap" class="search-results" tabindex="-1">
    <Spinner
      v-if="stationsApi.loading.value === true && !searchResponse?.data.length"
      size="large"
      color="primary"
    />
    <template v-else-if="searchResponse">
      <Summary v-if="props.showSummary">
        {{ $t('searchForm.summary', searchResponse.total) }}
      </Summary>
      <StationList
        v-if="searchResponse?.data.length"
        :aria-label="$t('searchResults.title')"
      >
        <StationListItem
          v-for="item of searchResponse.data"
          :key="item.id"
          ref="stationListItemComponents"
          :station="item"
          :show-country="props.showCountry"
          @click="(station) => setStation(station)"
        />
      </StationList>
      <Button
        v-if="
          searchResponse?.total_pages &&
          searchResponse.total_pages > currentPage
        "
        :text="$t('searchResults.loadMore')"
        :loading="stationsApi.loading.value === true"
        @click="currentPage++"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import type { SearchTerms, StationsResponse } from '@/types'
import { ref, useTemplateRef, computed, watch, nextTick } from 'vue'
import { setStation } from '@/lib/player'
import { useApi } from '@/lib/api'
import { watchPausable } from '@vueuse/core'
import StationListItem from '@/components/StationListItem.vue'

const props = withDefaults(
  defineProps<{
    q?: string
    tag?: string
    category?: string
    country?: string
    showSummary?: boolean
    showCountry?: boolean
  }>(),
  {
    q: '',
    tag: '',
    category: '',
    country: '',
    showSummary: true,
    showCountry: true,
    position: undefined,
  },
)

const searchResultsWrapEl = useTemplateRef<HTMLDivElement>('searchResultsWrap')
const currentPage = ref<number>(1)
const stationsApi = useApi()
const stationListItemComponents = useTemplateRef<
  InstanceType<typeof StationListItem>[]
>('stationListItemComponents')
const searchResponse = ref<StationsResponse | undefined>(undefined)

const reset = async () => {
  searchResponse.value = undefined

  pauseCurrentPageWatch()
  currentPage.value = 1
  await nextTick()
  resumeCurrentPageWatch()
}

const getResults = async (): Promise<StationsResponse | undefined> => {
  try {
    const response = await stationsApi.ky
      .get('stations', {
        searchParams: {
          ...searchTerms.value,
          page: currentPage.value,
        },
      })
      .json<StationsResponse>()

    return response
  } catch (error) {
    console.error(error)
    return undefined
  }
}

const search = async () => {
  await nextTick()
  await reset()

  // If there are no terms defined, bail out
  if (Object.values(searchTerms.value).every((item) => !item)) {
    reset()
    return
  }

  // Get results
  searchResponse.value = await getResults()
}

const searchTerms = computed<SearchTerms>(() => {
  return {
    q: props.q.trim(),
    tag: props.tag,
    category: props.category,
    country: props.country,
  }
})

watch(
  () => searchTerms.value,
  () => {
    search()
  },
  {
    immediate: true,
    deep: true,
  },
)

const { pause: pauseCurrentPageWatch, resume: resumeCurrentPageWatch } =
  watchPausable(
    () => currentPage.value,
    async () => {
      if (
        stationsApi.loading.value === true ||
        !searchResponse.value?.data.length
      ) {
        return
      }

      const nextPage = await getResults()
      if (!nextPage?.data?.length) {
        return
      }

      // Not sure why spread did not work here...
      searchResponse.value.data = searchResponse.value.data.concat(
        nextPage.data,
      )

      await nextTick()
      stationListItemComponents.value
        ?.find((item) => item.id === nextPage.data[0].id)
        ?.focus()
    },
  )

const focus = () => {
  searchResultsWrapEl.value?.focus()
}

defineExpose({
  focus,
})
</script>

<style lang="scss" scoped>
.search-results {
  width: 100%;
  outline: 0;
}
</style>
